import {defineAbilityConfig} from '../system/defineAbilityConfig';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';

export const canSync = defineAbilityConfig([
  {
    branches: {
      main: true,
    },
    permission: ['design', 'site'],
    abilities: {canEditStaticPageContent: true},
  },
] as const);

/**
 * Similar to `canSync` but includes additional abilities check when on a branch.
 * This is used for syncs that can also be performed on branches.
 */
export const canSyncOnBranch = defineAbilityConfig([
  // gates the main site syncs on the `site.design` permission and canEditStaticPageContent
  {
    branches: {
      main: true,
    },
    permission: ['design', 'site'],
    abilities: {canEditStaticPageContent: true},
  },
  // gates the branch syncs on the `siteBranch.design` permission (legacy mode)
  {
    abilities: {canAccessStaticPageContentFgac: false},
    branches: {
      branch: true,
    },
    permission: ['design', 'siteBranch'],
  },
  // gates the branch syncs on SiteBranch/design authorization (FGAC mode) and canAccessStaticPageContentFgac
  {
    abilities: {canAccessStaticPageContentFgac: true},
    branches: {
      branch: true,
    },
    authorizations: {
      'SiteBranch/design': true,
    },
  },
] as const);

/**
 * In multiplayer mode we only allow regular syncing in certain cases. The majority of the designer data
 * will be synced by the multiplayer-server so we want to disable regular sync to avoid overriding data.
 * Includes canEditStaticPageContent check for restricted pages.
 */
export const canSyncInMultiplayer = defineAbilityConfig([
  {
    featureFlags: {
      [FEATURE_FLAGS.REALTIME]: true,
      [FEATURE_FLAGS.MULTI_USER_BRANCHING]: false,
    },
    branches: {branch: true},
    abilities: {canEditBranchContent: true},
  },
] as const);

/**
 * Determines whether a user can perform a DOM sync, both in the multiplayer and non-multiplayer cases
 */
export const canDomSync = defineAbilityConfig([
  // non-multiplayer mode dom sync permissions are the same as the generic sync permissions
  {
    featureFlags: {[FEATURE_FLAGS.REALTIME]: false},
    // since DOM sync is a sync type that is enabled on branches, we use canSyncOnBranch instead of canSync
    abilities: {canSyncOnBranch: true},
  },

  // multiplayer dom syncs are only allowed in cases defined in canSyncInMultiplayer
  {
    featureFlags: {[FEATURE_FLAGS.REALTIME]: true},
    abilities: {canSyncInMultiplayer: true},
  },
] as const);

/**
 * Determines whether a user can perform a Styles sync, both in the multiplayer and non-multiplayer cases
 */
export const canCssSync = defineAbilityConfig([
  // non-multiplayer mode Styles sync permissions are the same as the generic sync permissions
  {
    featureFlags: {[FEATURE_FLAGS.REALTIME]: false},
    // since Styles sync is a sync type that is enabled on branches, we use canSyncOnBranch instead of canSync
    abilities: {canSyncOnBranch: true},
  },

  // multiplayer styles syncs are only allowed in cases defined in canSyncInMultiplayer
  {
    featureFlags: {[FEATURE_FLAGS.REALTIME]: true},
    abilities: {canSyncInMultiplayer: true},
  },
] as const);

/**
 * Determines whether a user can perform a Variables sync. Variables sync is only allowed in non-multiplayer mode;
 * there are no scenarios where a designer session can sync variables in multiplayer mode.
 */
export const canVariablesSync = defineAbilityConfig({
  featureFlags: {[FEATURE_FLAGS.REALTIME]: false},
  // since Variables sync is a sync type that is NOT enabled on branches, we use the canSync ability instead of canSyncOnBranch
  abilities: {canSync: true},
} as const);

/**
 * Determines whether a user can perform an IX2 sync. IX2 sync on main is allowed in both multiplayer and non-multiplayer mode;
 * however for branches with multi-user branching enabled, it is not allowed because IX2 is unsupported.
 */
export const canIX2Sync = defineAbilityConfig([
  {
    branches: {main: true},
    permission: ['manage', 'siteVariable'],
  },
  // safety clause when in branch:
  // in case REALTIME is false with MULTI_USER_BRANCHING true, it's treated as MULTI_USER_BRANCHING false
  {
    featureFlags: {[FEATURE_FLAGS.REALTIME]: false},
    branches: {branch: true},
    abilities: {canSyncOnBranch: true},
  },
  {
    featureFlags: {[FEATURE_FLAGS.MULTI_USER_BRANCHING]: false},
    branches: {branch: true},
    abilities: {canSyncOnBranch: true},
  },
] as const);

/**
 * Determines whether a user can perform an IX3 sync. IX3 sync is only allowed in non-multiplayer mode;
 * there are no scenarios where a designer session can sync IX3 data in multiplayer mode.
 */
export const canIX3Sync = defineAbilityConfig({
  featureFlags: {[FEATURE_FLAGS.REALTIME]: false},
  abilities: {canSync: true},
} as const);
